import { Application, Container } from 'pixi.js';
import { TARGET_FPS, CANVAS_LIMITS } from '@wooorms/shared';
import { PerformanceMonitor } from '../utils/PerformanceMonitor';
import { Camera } from './Camera';
import { World } from './World';

/**
 * Layer names for organizing game objects
 */
export enum GameLayer {
	BACKGROUND = 'background',
	FOOD = 'food',
	WORMS = 'worms',
	UI = 'ui',
}

/**
 * Main game class that manages the PixiJS application,
 * game loop, layers, and core systems.
 */
export class Game {
	/** PixiJS Application instance */
	public app: Application;

	/** Camera system for viewport management */
	public camera: Camera;

	/** World renderer for background and boundaries */
	public world: World;

	/** Container for all game world objects (affected by camera) */
	public worldContainer: Container;

	/** Container for UI elements (not affected by camera) */
	public uiContainer: Container;

	/** Game layer containers */
	private layers: Map<GameLayer, Container> = new Map();

	/** Whether the game has been initialized */
	private initialized = false;

	/** Delta time accumulator for fixed timestep updates */
	private accumulator = 0;

	/** Fixed timestep for physics/game updates (in seconds) */
	private readonly fixedDeltaTime = 1 / TARGET_FPS;

	/** Bound resize handler for cleanup */
	private boundResizeHandler: (() => void) | null = null;

	constructor() {
		this.app = new Application();
		this.worldContainer = new Container();
		this.uiContainer = new Container();
		this.camera = new Camera(this);
		this.world = new World(this);
	}

	/**
	 * Initialize the game application
	 */
	async init(): Promise<void> {
		if (this.initialized) {
			console.warn('Game already initialized');
			return;
		}

		// Calculate initial canvas size with limits
		const { width, height } = this.getCanvasSize();

		// Initialize PixiJS application with WebGL
		// Performance: Disable antialiasing and limit resolution for better FPS
		await this.app.init({
			background: 0x0a0a1a,
			width,
			height,
			antialias: false, // Performance: Disable antialiasing
			resolution: Math.min(window.devicePixelRatio || 1, 2), // Performance: Cap at 2x
			autoDensity: true,
			preference: 'webgl',
			powerPreference: 'high-performance', // Performance: Request high-performance GPU
		});

		// Add canvas to DOM
		const container = document.getElementById('game-container');
		if (!container) {
			throw new Error('Game container element not found');
		}
		container.appendChild(this.app.canvas);

		// Style container for centering when canvas is smaller than window
		container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background: #0a0a1a;
      overflow: hidden;
    `;

		// Setup containers and layers
		this.setupLayers();

		// Initialize camera
		this.camera.init();

		// Initialize world renderer
		await this.world.init();

		// Setup resize handler
		this.setupResizeHandler();

		// Setup game loop
		this.setupGameLoop();

		this.initialized = true;
	}

	/**
	 * Setup game layers for organizing display objects
	 */
	private setupLayers(): void {
		// Add world container (affected by camera transforms)
		this.app.stage.addChild(this.worldContainer);

		// Create layer containers within world container
		// Food rendered on top of worms so it's always visible
		const layerOrder = [
			GameLayer.BACKGROUND,
			GameLayer.WORMS,
			GameLayer.FOOD,
		];

		for (const layerName of layerOrder) {
			const layer = new Container();
			layer.label = layerName;
			this.layers.set(layerName, layer);
			this.worldContainer.addChild(layer);
		}

		// UI container sits on top, not affected by camera
		this.uiContainer.label = GameLayer.UI;
		this.layers.set(GameLayer.UI, this.uiContainer);
		this.app.stage.addChild(this.uiContainer);
	}

	/**
	 * Get a layer container by name
	 */
	getLayer(name: GameLayer): Container {
		const layer = this.layers.get(name);
		if (!layer) {
			throw new Error(`Layer not found: ${name}`);
		}
		return layer;
	}

	/**
	 * Setup window resize handler
	 */
	private setupResizeHandler(): void {
		this.boundResizeHandler = () => {
			this.onResize();
		};
		window.addEventListener('resize', this.boundResizeHandler);
		// Trigger initial resize
		this.onResize();
	}

	/**
	 * Calculate canvas size with optional limits for ultra-wide monitors
	 */
	private getCanvasSize(): { width: number; height: number } {
		let width = window.innerWidth;
		let height = window.innerHeight;

		// Apply canvas size limits if enabled
		if (CANVAS_LIMITS.ENABLED) {
			width = Math.min(width, CANVAS_LIMITS.MAX_WIDTH);
			height = Math.min(height, CANVAS_LIMITS.MAX_HEIGHT);
		}

		return { width, height };
	}

	/**
	 * Handle window resize
	 */
	private onResize(): void {
		const { width, height } = this.getCanvasSize();

		// Resize the PixiJS renderer
		this.app.renderer.resize(width, height);

		// Update camera with new screen dimensions
		this.camera.onResize(width, height);

		// Update world renderer
		this.world.onResize(width, height);
	}

	/**
	 * Setup the main game loop using PixiJS ticker
	 */
	private setupGameLoop(): void {
		const monitor = PerformanceMonitor.getInstance();

		// Hook into rendering events for measurement
		(this.app.renderer as any).on('prerender', () => {
			monitor.startMeasure('render');
		});
		(this.app.renderer as any).on('postrender', () => {
			monitor.endMeasure('render');
		});

		this.app.ticker.add((ticker) => {
			// Get delta time in seconds
			const deltaTime = ticker.deltaMS / 1000;

			monitor.startMeasure('update');

			// Fixed timestep update for physics/game logic
			this.accumulator += deltaTime;
			while (this.accumulator >= this.fixedDeltaTime) {
				this.fixedUpdate(this.fixedDeltaTime);
				this.accumulator -= this.fixedDeltaTime;
			}

			// Variable timestep update for rendering/interpolation
			this.update(deltaTime);

			monitor.endMeasure('update');
			monitor.update(deltaTime);
		});
	}

	/**
	 * Fixed timestep update - use for physics and game logic
	 * @param _dt Fixed delta time in seconds
	 */
	fixedUpdate(_dt: number): void {
		// Override in subclass or add game logic here
	}

	/**
	 * Variable timestep update - use for rendering and interpolation
	 * @param dt Delta time in seconds
	 */
	update(dt: number): void {
		// Update camera
		this.camera.update(dt);

		// Update world rendering with current camera zoom for grid scaling
		this.world.update(dt, this.camera.getZoom());
	}

	/**
	 * Get screen dimensions
	 */
	get screenWidth(): number {
		return this.app.screen.width;
	}

	get screenHeight(): number {
		return this.app.screen.height;
	}

	/**
	 * Get center of screen
	 */
	get screenCenter(): { x: number; y: number } {
		return {
			x: this.screenWidth / 2,
			y: this.screenHeight / 2,
		};
	}

	/**
	 * Destroy the game and clean up resources
	 */
	destroy(): void {
		this.app.ticker.stop();
		this.world.destroy();
		this.camera.destroy();

		// Remove resize listener
		if (this.boundResizeHandler) {
			window.removeEventListener('resize', this.boundResizeHandler);
			this.boundResizeHandler = null;
		}

		// Destroy all layers
		for (const layer of this.layers.values()) {
			layer.destroy({ children: true });
		}
		this.layers.clear();

		// Destroy app
		this.app.destroy(true, { children: true });
		this.initialized = false;
	}
}
